import { useNavigate } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { Reveal, RevealStagger, RevealItem } from "@/components/shared/Reveal";
import { ChapterHeading } from "@/components/shared/ChapterHeading";
import { StatStrip } from "@/components/shared/StatStrip";
import { IconTile } from "@/components/shared/IconTile";
import { SPONSOR_AUDIENCE, PARTNERSHIP_POSSIBILITIES, PARTNERSHIP_APPROACH, COLORS, SPONSOR_BANNER_IMAGES } from "@/data/siteData";

export default function Sponsor() {
  useSEO(
    "Sponsor & Collaborate | The Market Place Jaipur",
    "Partner with The Market Place — a platform that has spent years building relationships with Jaipur's entrepreneurs, homegrown businesses and discovery-led consumers."
  );
  const navigate = useNavigate();

  return (
    <div data-testid="sponsor-page">
      <section className="pt-16 pb-16 px-6 sm:px-8 max-w-4xl mx-auto text-center">
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#D81B60]">Sponsor & Collaborate</span>
          <h1 className="font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight">
            Build Something Meaningful With The Market Place.
          </h1>
          <p className="mt-6 text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Partner with a platform that has spent years building relationships with Jaipur's entrepreneurs,
            homegrown businesses and discovery-led consumers.
          </p>
          <button
            onClick={() => navigate("/contact?type=Sponsorship")}
            data-testid="sponsor-partner-with-us-button"
            className="mt-8 rounded-full bg-[#D81B60] text-white px-8 py-4 text-sm font-semibold hover:bg-[#AD144B] transition-colors duration-300"
          >
            Partner With Us
          </button>
        </Reveal>
      </section>

      {/* Established community */}
      <section className="py-12 sm:py-16 px-6 sm:px-8">
        <div className="max-w-5xl mx-auto rounded-[2rem] bg-[#1A1A1A] text-white p-10 sm:p-14 text-center">
          <Reveal>
            <h2 className="font-extrabold text-3xl sm:text-4xl mb-6">An Established Community</h2>
            <p className="text-white/70 max-w-2xl mx-auto mb-8">Since 2019, The Market Place has built consistent on-ground engagement through:</p>
          </Reveal>
          <StatStrip dark />
          <Reveal delay={0.2}>
            <p className="mt-8 text-[#F48FB1] font-semibold max-w-xl mx-auto">
              For partners, this creates access to an existing ecosystem rather than an audience assembled for a single event.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Why The Market Place */}
      <section className="py-12 sm:py-16 px-6 sm:px-8 max-w-6xl mx-auto">
        <ChapterHeading number="01" title="Why The Market Place" subtitle="The Market Place sits at the intersection of entrepreneurship, lifestyle, commerce and community. Partners can engage with:" />
        <RevealStagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SPONSOR_AUDIENCE.map((a, i) => (
            <RevealItem key={a.title}>
              <div className="rounded-xl border border-gray-200 p-6 h-full text-center">
                <IconTile icon={a.icon} title={a.title} color={COLORS[i % COLORS.length]} center />
              </div>
            </RevealItem>
          ))}
        </RevealStagger>
        <Reveal><p className="text-center mt-10 text-gray-500 italic">The environment allows brands to move beyond passive advertising and interact with people physically.</p></Reveal>
      </section>

      {/* Partnership Possibilities */}
      <section className="py-12 sm:py-16 px-6 sm:px-8 bg-[#FDFBF7]">
        <div className="max-w-6xl mx-auto">
          <ChapterHeading number="02" title="Partnership Possibilities" />
          <RevealStagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PARTNERSHIP_POSSIBILITIES.map((p, i) => (
              <RevealItem key={p.title}>
                <div className="rounded-2xl border border-gray-200 bg-white p-7 h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                  <IconTile icon={p.icon} title={p.title} desc={p.desc} color={COLORS[i % COLORS.length]} />
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-12 sm:py-16 px-6 sm:px-8 max-w-4xl mx-auto">
        <ChapterHeading number="03" title="Our Approach to Partnerships" subtitle="We believe the best collaborations add value to everyone involved." />
        <div className="space-y-5">
          {PARTNERSHIP_APPROACH.map((line, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <p className="text-lg sm:text-xl text-gray-700 border-l-4 pl-6" style={{ borderColor: COLORS[i % COLORS.length] }}>{line}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Community in action */}
      <section className="py-12 sm:py-16 px-6 sm:px-8 max-w-6xl mx-auto">
        <div className="grid sm:grid-cols-2 gap-6">
          {SPONSOR_BANNER_IMAGES.map((img, i) => (
            <Reveal key={img} delay={i * 0.08}>
              <div className="rounded-2xl overflow-hidden">
                <img src={img} alt="Community engagement at The Market Place, Jaipur" className="w-full aspect-[16/10] object-cover" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Partner With Us closing */}
      <section className="pb-16 px-6 sm:px-8 max-w-3xl mx-auto text-center">
        <Reveal>
          <h2 className="font-extrabold text-3xl sm:text-4xl mb-4">Partner With Us</h2>
          <p className="text-gray-500 mb-8 leading-relaxed">
            Whether you are exploring sponsorship, an experiential activation, a collaborative market, community
            initiative or institutional partnership, we would be glad to explore what can be built together.
          </p>
          <button
            onClick={() => navigate("/contact?type=Sponsorship")}
            data-testid="sponsor-start-conversation-button"
            className="rounded-full bg-[#D81B60] text-white px-10 py-4 text-sm font-semibold hover:bg-[#AD144B] transition-colors duration-300"
          >
            Start a Conversation
          </button>
        </Reveal>
      </section>
    </div>
  );
}
